// ────────────────────────────────────────────────────────────────────
// WHICH CANDIDATE IS IN FORCE — matched on VALUES, never on identity.
//
// THE BUG THIS FILE EXISTS FOR. The ledger under the rate strip marked its
// winner with `c === in_force`. In a test that reads fine: `dueAssessment()`
// built one object and put it in `candidates` AND in `in_force`, so the two
// references were the same reference. On the wire they are not. FastAPI
// serialises `DueAssessment.in_force` and the matching element of `candidates`
// as two separate JSON objects (server/routers/integrity.py DueAssessmentOut),
// axios `JSON.parse`s the body, and `===` is then false for every candidate on
// every real record. The panel the whole sub-project exists for — "WHY THIS
// DATE, AND NOT THE NOMINAL ONE" — rendered a measured date and an INFERRED
// profile default side by side with nothing saying which one governs, while
// the NEXT INSPECTION cell above it still printed the right date. Confidently
// wrong rather than blank.
//
// SO THE MATCH IS ON FIELDS THE WIRE PRESERVES. Origin alone is not enough:
// rules.ORIGINS' own comment records that one asset really can carry two
// `profile` candidates (the reference table's current figure and the
// provisional copy seeded onto the asset), so the origin plus the VALUE is the
// narrowest pair that survives serialisation. `basis` is compared too — it is
// the sentence that distinguishes two same-origin candidates a reader is being
// asked to tell apart.
//
// AND THE ANSWER IS AN INDEX, not a boolean per row. Two candidates that agree
// on all three fields are the same answer twice; marking both IN FORCE would
// say the system holds two verdicts. The FIRST match wins, which is also what
// the server means: `assess_interval` returns `ordered` with `ordered[0]` in
// force and `assess_due` dates that list in place (rules.py:283, :400).
//
// NOTHING HERE DECIDES ANYTHING. `in_force` is the server's verdict; this file
// only finds which row on screen it refers to.
// ────────────────────────────────────────────────────────────────────
import type {
  DueAssessment, DueCandidate, IntervalAssessment, IntervalCandidate,
} from "./types";

function firstMatch<T>(candidates: readonly T[], match: (c: T) => boolean): number {
  for (let i = 0; i < candidates.length; i += 1) {
    if (match(candidates[i])) return i;
  }
  return -1;
}

/** Index of the due candidate the record says governs, or -1 when the record
 *  names none (`in_force: null` — "no due date could be established"). */
export function dueInForceIndex(due: DueAssessment): number {
  const w: DueCandidate | null = due.in_force;
  if (w == null) return -1;
  return firstMatch(due.candidates,
                    (c) => c.due === w.due && c.origin === w.origin && c.basis === w.basis);
}

/** The same question for the interval assessment. The candidate ledger is
 *  drawn from the DUE assessment today, but the interval one carries the same
 *  winner-among-losers shape and reached this screen through the same wire —
 *  a second reader of it must not have to rediscover why `===` is wrong. */
export function intervalInForceIndex(interval: IntervalAssessment): number {
  const w: IntervalCandidate | null = interval.in_force;
  if (w == null) return -1;
  return firstMatch(interval.candidates,
                    (c) => c.months === w.months && c.origin === w.origin
                        && c.basis === w.basis);
}
